// ─────────────────────────────────────────────────────────────────────────────
// Pictionary — DrawingBackground (decorative SVG)
// Full-bleed, pointer-transparent scene of pencils, paint splashes, strokes and
// sparkles in the Pictionary palette, matching the rail-track background used on
// the Ticket to Ride landing page and the gem background on Splendor's.
// ─────────────────────────────────────────────────────────────────────────────

export default function DrawingBackground() {
  return (
    <svg
      className="absolute inset-0 h-full w-full select-none pointer-events-none"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        {/* Slanted pencil — body colored via fill, tip always wood+lead */}
        <g id="draw-pencil">
          <rect x="-60" y="-7" width="120" height="14" rx="3" />
          <path d="M-68 -7 L-88 0 L-68 7 Z" fill="#e8b46a" />
          <rect x="-90" y="-2.5" width="6" height="5" rx="1" fill="#2b2b33" />
        </g>

        {/* Crayon stub */}
        <g id="draw-crayon">
          <rect x="-46" y="-9" width="92" height="18" rx="5" />
          <path d="M-42 -9 L-50 0 L-42 9 Z" fill="#20233a" opacity="0.85" />
        </g>

        {/* Four-point sparkle */}
        <g id="draw-sparkle">
          <path d="M0 -10 L2.4 -2.4 L10 0 L2.4 2.4 L0 10 L-2.4 2.4 L-10 0 L-2.4 -2.4 Z" />
        </g>

        {/* Isolated paint splat */}
        <g id="draw-splat">
          <circle r="7" />
          <circle cx="9" cy="-5" r="3.4" />
          <circle cx="-8" cy="6" r="2.6" />
          <circle cx="5" cy="8" r="2" />
          <circle cx="-9" cy="-7" r="2.2" />
        </g>

        {/* Squiggly sketch stroke */}
        <path
          id="draw-squiggle"
          d="M0 0 C18 -14 36 14 54 0 C72 -14 90 14 108 0 C126 -14 144 10 160 -2"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </defs>

      {/* Soft color washes */}
      <ellipse cx="720" cy="280" rx="640" ry="400" fill="#10b981" opacity="0.07" />
      <ellipse cx="150" cy="800" rx="460" ry="320" fill="#8b5cf6" opacity="0.07" />
      <ellipse cx="1310" cy="140" rx="440" ry="320" fill="#f59e0b" opacity="0.06" />

      {/* Doodle strokes */}
      <g stroke="#8b5cf6" opacity="0.11">
        <use href="#draw-squiggle" transform="translate(120 190) scale(1.4)" />
        <use href="#draw-squiggle" transform="translate(300 260) rotate(18) scale(1.3)" />
      </g>
      <g stroke="#10b981" opacity="0.10">
        <use href="#draw-squiggle" transform="translate(1060 700) scale(1.2) rotate(-8)" />
        <use href="#draw-squiggle" transform="translate(1230 440) rotate(24) scale(1)" />
      </g>
      <g stroke="#f59e0b" opacity="0.09">
        <use href="#draw-squiggle" transform="translate(880 120) scale(0.9)" />
        <use href="#draw-squiggle" transform="translate(540 620) rotate(-14) scale(1.1)" />
      </g>

      {/* ── Pencils scattered ── */}
      <g transform="translate(140 700) rotate(-12) scale(1.1)">
        <use href="#draw-pencil" fill="#d2264f" opacity="0.32" />
      </g>
      <g transform="translate(1330 690) rotate(24) scale(1.0)">
        <use href="#draw-pencil" fill="#2b63e8" opacity="0.32" />
      </g>
      <g transform="translate(320 160) rotate(14) scale(0.85)">
        <use href="#draw-pencil" fill="#e0a60f" opacity="0.28" />
      </g>
      <g transform="translate(1240 200) rotate(-18) scale(0.9)">
        <use href="#draw-pencil" fill="#12a566" opacity="0.28" />
      </g>
      <g transform="translate(960 740) rotate(38) scale(0.8)">
        <use href="#draw-pencil" fill="#8b5cf6" opacity="0.32" />
      </g>

      {/* ── Crayons ── */}
      <g transform="translate(84 360) rotate(18) scale(0.85)">
        <use href="#draw-crayon" fill="#e0a60f" opacity="0.26" />
      </g>
      <g transform="translate(1400 320) rotate(-24) scale(0.8)">
        <use href="#draw-crayon" fill="#d2264f" opacity="0.26" />
      </g>
      <g transform="translate(680 130) rotate(-8) scale(0.72)">
        <use href="#draw-crayon" fill="#2b63e8" opacity="0.24" />
      </g>

      {/* ── Paint splats ── */}
      <g transform="translate(640 300) scale(2.2)">
        <use href="#draw-splat" fill="#8b5cf6" opacity="0.2" />
      </g>
      <g transform="translate(1180 560) scale(1.8)">
        <use href="#draw-splat" fill="#10b981" opacity="0.2" />
      </g>
      <g transform="translate(260 460) scale(1.6)">
        <use href="#draw-splat" fill="#f59e0b" opacity="0.18" />
      </g>
      <g transform="translate(920 180) scale(1.5)">
        <use href="#draw-splat" fill="#d2264f" opacity="0.17" />
      </g>

      {/* ── Sparkles ── */}
      <g transform="translate(520 780) scale(0.9)">
        <use href="#draw-sparkle" fill="#8b5cf6" opacity="0.32" />
      </g>
      <g transform="translate(1120 90) scale(0.8)">
        <use href="#draw-sparkle" fill="#8b5cf6" opacity="0.28" />
      </g>
      <g transform="translate(90 560) scale(0.7)">
        <use href="#draw-sparkle" fill="#10b981" opacity="0.32" />
      </g>
      <g transform="translate(1380 540) scale(0.75)">
        <use href="#draw-sparkle" fill="#f59e0b" opacity="0.32" />
      </g>
      <g transform="translate(760 660) scale(0.6)">
        <use href="#draw-sparkle" fill="#2b63e8" opacity="0.28" />
      </g>
    </svg>
  );
}